import { ParkingPass } from '@m/types/api/ParkingPass';
import {
  PassModificationPreviewResponse,
  PassPlanPreview,
  PassReceiptResponse,
} from '@m/types/api/pass/SelfServiceParkingPassPlan';
import { GrpcCurrency, GrpcTimestamp } from '@m/types/grpc/GrpcTypes';
import { apiFetch } from '@m/utils/http';

export const SelfServiceParkingPassService = {
  getPasses: ({ from }: { from: GrpcTimestamp }) =>
    apiFetch<{ parkingPasses: ParkingPass[] }>('/customer/passes', { params: { from } }),

  getPass: ({ passUuid }: { passUuid: string }) =>
    apiFetch<{ parkingPass: ParkingPass }>(`/customer/passes/${passUuid}`),

  previewPassPlans: ({
    siteUuid,
    requestedConfigurations,
    discount,
  }: {
    discount?: {
      validationQrUuid: string;
    };
    requestedConfigurations: {
      [index: number]: {
        from: string;
        to: string;
      };
    };
    siteUuid: string;
  }) =>
    apiFetch<{ passPlanConfigurations: Record<number, PassPlanPreview> }>(
      `/v2/sites/${siteUuid}/pass-plans/preview`,
      {
        method: 'POST',
        body: {
          zoneId: siteUuid,
          requestedConfigurations,
          discount,
        },
      },
    ),

  purchasePassPlan: ({
    visitUuid,
    planUuid,
    plateText,
    plateState,
    from,
    to,
    customerId,
    paymentMethodId,
    discount,
  }: {
    customerId: number;
    discount?: {
      validationQrUuid: string;
    };
    from: GrpcTimestamp;
    paymentMethodId: number;
    planUuid: string;
    plateState: string;
    plateText: string;
    to: GrpcTimestamp;
    visitUuid: string;
  }) =>
    apiFetch<{ invoiceUuids: string[]; passUuid: string }>(
      `/customer/visit/${visitUuid}/parking-pass/purchase`,
      {
        method: 'POST',
        body: {
          coverage: {
            licensePlate: {
              licensePlateText: plateText,
              licensePlateState: plateState,
            },
          },
          planUuid,
          from,
          to,
          customerId,
          paymentMethodId,
          discount,
        },
      },
    ),

  previewParkingPassModifications: ({
    visitUuid,
    passUuid,
    options,
  }: {
    options?: {
      from: GrpcTimestamp;
      to: GrpcTimestamp;
    };
    passUuid: string;
    visitUuid: string;
  }) =>
    apiFetch<PassModificationPreviewResponse>(
      `/customer/visit/${visitUuid}/parking-pass/${passUuid}/preview-modifications`,
      {
        params: options ? { from: options.from, to: options.to } : undefined,
      },
    ),

  executeParkingPassModification: ({
    visitUuid,
    passUuid,
    to,
    paymentMethodId,
    customerId,
    passPrice,
  }: {
    customerId: number;
    passPrice: GrpcCurrency;
    passUuid: string;
    paymentMethodId: number;
    to: string; // ISO
    visitUuid: string;
  }) =>
    apiFetch<{ invoiceUuids: string[] }>(
      `/customer/visit/${visitUuid}/parking-pass/${passUuid}/modify`,
      {
        method: 'POST',
        body: {
          passUuid,
          to,
          paymentMethodId,
          customerId,
          passPrice,
        },
      },
    ),

  getParkingPassReceipt: ({ passUuid }: { passUuid: string }) =>
    apiFetch<PassReceiptResponse>(`/customer/passes/${passUuid}/receipt`),

  getPassValidationUUID: ({ signCode }: { signCode: string }) =>
    apiFetch<{ validationQrUUID: string }>(`/customer/validation-code`, {
      method: 'POST',
      body: { signCode },
    }),

  deleteValidationCode: ({ validationQrUUID }: { validationQrUUID?: string }) =>
    validationQrUUID
      ? apiFetch(`/customer/validation/${validationQrUUID}`, {
          method: 'DELETE',
        })
      : Promise.resolve({ success: false, data: null, error: 'No validationQrUUID provided' }),
};
